import { onSessionExpired } from "services/users/auth";
import Configuration from "../config";
import { logError } from "./logging";
import { ApiRequestError } from "./errors";

export const API_BASE_URL = Configuration.API_BASE_URL + "api";

export type RequestOptions = {
  headers?: Record<string, string>;
  onUnauthorized?: (serverUserId: string | null) => void;
  credentials?: RequestCredentials;
};

// Nullish values are omitted.
// Arrays are comma-joined. By convention backend expects a single comma-separated
// value for list params by convention
export type QueryParamValue =
  | string
  | number
  | boolean
  | ReadonlyArray<string | number>
  | null
  | undefined;
export type QueryParams = Record<string, QueryParamValue>;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export type RequestParams = RequestOptions & {
  query?: QueryParams | string | null;
  body?: unknown;
};

/**
 * The transport: builds the URL, sends, and turns a refusal into an
 * ApiRequestError. The Response comes back undecoded, for the callers that need
 * the status too — /dictionary/examples answers 204 while it is still generating,
 * and the content endpoints serve XML or HTML. `api` below is this plus decoding.
 */
export async function sendRequest(
  method: HttpMethod,
  path: string,
  params: RequestParams = {}
): Promise<Response> {
  const {
    query,
    body,
    headers: extraHeaders,
    onUnauthorized = onSessionExpired,
    credentials = "include",
  } = params;

  const urlString = buildUrl(path, query);

  const headers: Record<string, string> = {
    Accept: "application/json",
    ...extraHeaders,
  };

  let fetchBody: BodyInit | undefined;
  if (body !== undefined && body !== null) {
    const contentType = headers["Content-Type"];
    if (contentType && !contentType.includes("application/json")) {
      fetchBody = body as BodyInit;
    } else {
      fetchBody = JSON.stringify(body);
      if (!contentType) {
        headers["Content-Type"] = "application/json";
      }
    }
  }

  try {
    const response = await fetch(urlString, {
      method,
      headers,
      body: fetchBody,
      credentials,
    });

    if (!response.ok) {
      throw await toApiError(response, `${method} ${urlString}`, onUnauthorized);
    }

    return response;
  } catch (error) {
    if (error instanceof ApiRequestError) {
      throw error;
    }

    // Everything left is the request not completing at all: no network, or an
    // abort. A body that is not what it claimed is decodeJson's to report.
    logError(`Unexpected API request error: ${method} ${urlString}`, { err: error });
    throw new ApiRequestError(
      undefined,
      undefined,
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

// A refusal, read for the body that explains it and reported, ready to be thrown.
async function toApiError(
  response: Response,
  request: string,
  onUnauthorized: (serverUserId: string | null) => void
): Promise<ApiRequestError> {
  // The empty handler is necessary: .catch() with no handler is a no-op.
  const body: unknown = await response
    .clone()
    .json()
    .catch(() => undefined);

  const error = new ApiRequestError(response, body);
  if (error.isUnauthorized) {
    // The server sends this header empty, not absent, when the request had no user.
    onUnauthorized(response.headers.get(USER_ID_HEADER) || null);
  }
  if (response.status >= 500) {
    logError(`Unexpected API request error: ${request}`, { err: response });
  }
  return error;
}

/**
 * A read asked for a body, so an empty or non-JSON one is a broken response. A
 * write is the other way round: 204 is how this API acknowledges one, and its
 * TResponse defaults to void.
 */
async function decodeJson<TResponse>(
  response: Response,
  method: HttpMethod,
  path: string
): Promise<TResponse> {
  const contentType = response.headers.get("content-type");
  // Both checks needed: a 204 never carries content-length (RFC 7230).
  const empty = response.status === 204 || response.headers.get("content-length") === "0";
  const json = contentType?.includes("application/json") ?? false;

  if (empty || !json) {
    if (method === "GET") {
      throw new ApiRequestError(
        response,
        undefined,
        new Error(
          empty
            ? `${method} ${path} answered with no body`
            : `${method} ${path} answered with ${contentType ?? "no content-type"}, not JSON`
        )
      );
    }
    return undefined as TResponse;
  }

  // Awaited so a body that is not the JSON the response claimed is caught here
  // rather than surfacing to the caller as a rejected json() promise.
  try {
    return (await response.json()) as TResponse;
  } catch (error) {
    logError(`Unexpected API request error: ${method} ${path}`, { err: error });
    throw new ApiRequestError(
      response,
      undefined,
      error instanceof Error ? error : new Error(String(error))
    );
  }
}

async function send<TResponse>(
  method: HttpMethod,
  path: string,
  params: RequestParams
): Promise<TResponse> {
  const response = await sendRequest(method, path, params);
  return decodeJson<TResponse>(response, method, path);
}

/**
 * The JSON API: every method decodes the body into the type the caller named.
 *
 * `TQueryParams` looks inert but is how a caller names the query's own type and
 * gets excess-property checking on it. QueryParams alone is a bare index
 * signature, which accepts any key.
 */
export const api = {
  get: <TResponse, TQueryParams extends QueryParams | void = QueryParams>(
    path: string,
    query?: TQueryParams | null,
    config?: RequestOptions
  ): Promise<TResponse> => {
    return send<TResponse>("GET", path, { ...config, query: query ?? null });
  },

  post: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return send<TResponse>("POST", path, { ...config, query, body: data });
  },

  put: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return send<TResponse>("PUT", path, { ...config, query, body: data });
  },

  patch: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return send<TResponse>("PATCH", path, { ...config, query, body: data });
  },

  delete: <TPayload, TResponse = void, TQueryParams extends QueryParams = QueryParams>(
    path: string,
    data?: TPayload,
    options?: WriteOptions<TQueryParams>
  ): Promise<TResponse> => {
    const { query, ...config } = options ?? {};
    return send<TResponse>("DELETE", path, { ...config, query, body: data });
  },
};

type WriteOptions<TQueryParams extends QueryParams = QueryParams> = RequestOptions & {
  query?: TQueryParams;
};

function buildUrl(path: string, query: QueryParams | string | null | undefined): string {
  const base = API_BASE_URL + path;
  if (!query) {
    return base;
  }
  const qs =
    typeof query === "string"
      ? query
      : new URLSearchParams(
          Object.entries(query)
            .filter(([, value]) => value !== null && value !== undefined)
            .map(([key, value]) => [
              key,
              Array.isArray(value) ? value.join() : String(value),
            ])
        ).toString();
  return qs ? `${base}?${qs}` : base;
}

const USER_ID_HEADER = "x-user-id";

export { HTTP_CODES } from "./errors";
